import { useCallback } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useSiteTraits } from '@confluence/targeting/entry-points/useSiteTraits.ts';

const LOOM_AUTO_PROVISION_TRAIT_NAME = 'loom.auto-provision.activationDate';
const LOOM_TWC_ENABLEMENT_TRAIT_NAME = 'loom.twc-enablement.activationDate';

export const useShouldShowLoomSpotlight = (): {
	shouldShowSpotlight: boolean;
	setSeenSpotlight: () => void;
} => {
	// 1. check if a Loom provisioning trait is set against SITE
	const {
		data: siteTraits,
		loading: areTraitsLoading,
		error: traitsError,
	} = useSiteTraits({
		skipBootstrap: false,
	});
	const hasSiteAutoProvisionTrait =
		siteTraits?.attributes?.some((attr) => attr.name === LOOM_AUTO_PROVISION_TRAIT_NAME) ?? false;
	const hasSiteTwcEnablementTrait =
		siteTraits?.attributes?.some((attr) => attr.name === LOOM_TWC_ENABLEMENT_TRAIT_NAME) ?? false;
	const hasGrantedLoomAccessDateTrait = hasSiteAutoProvisionTrait || hasSiteTwcEnablementTrait;

	// Traits read as absent until they resolve, and the store starts at
	// `{ data: null, loading: false }` — so `loading` alone does not prove resolution.
	const areTraitsResolved = !areTraitsLoading && (siteTraits !== null || traitsError !== null);

	// 2. check if there is 'seen' value set in the local storage (persisted to backend)
	const hasSeenSpotlight = !!localStorage.getItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_AUTO_PROVISION_SPOTLIGHT_SEEN,
	);

	// 3. fall back to the co-use experiments. Every other eligibility condition is
	// checked first so expVal() — and the Statsig exposure it logs — is only reached
	// by users who could actually be shown the spotlight.
	const isExperimentEnabled =
		areTraitsResolved &&
		!hasGrantedLoomAccessDateTrait &&
		!hasSeenSpotlight &&
		(expVal('exp_confluence_expand_loom_nudges_to_couse_ht', 'isEnabled', false) ||
			expVal('exp_expand_loom_nudges_to_couse_lt', 'isEnabled', false));

	const shouldShowSpotlight =
		(hasGrantedLoomAccessDateTrait || isExperimentEnabled) && !hasSeenSpotlight;

	const setSeenSpotlight = useCallback((): void => {
		localStorage.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_AUTO_PROVISION_SPOTLIGHT_SEEN,
			'true',
		);
	}, []);

	return { shouldShowSpotlight, setSeenSpotlight };
};
